/**
 * tRPC client.
 *
 * `AppRouter` is imported as a *type* from the API package, so every procedure
 * name, input and output is checked at compile time with no code generation and
 * no schema file to drift. Nothing from the server reaches the bundle.
 */

import { QueryClient } from "@tanstack/react-query";
import {
  createTRPCClient,
  httpBatchLink,
  type TRPCClientErrorLike,
} from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import type { AppRouter } from "@fc/api/trpc/router";
import {
  ensureFreshSession,
  getAccessToken,
  notifyExpired,
  refreshSession,
} from "./session.js";

/**
 * Where the API lives.
 *
 * Defaults to port 8080 on whatever host served this page, rather than a
 * hardcoded `localhost`. That one line is what lets a phone on the same WiFi
 * open `http://192.168.1.20:5173` and have it talk to the API on the laptop —
 * `localhost` on a phone is the phone itself.
 *
 * Set `VITE_API_URL` to override, which production builds do.
 */
export const API_URL =
  import.meta.env.VITE_API_URL ??
  (typeof window !== "undefined"
    ? `${window.location.protocol}//${window.location.hostname}:8080`
    : "http://localhost:8080");

/**
 * Attaches the access token, refreshes it when stale, and retries exactly once
 * on a 401 — which covers the case where the server revoked a session between
 * our expiry estimate and the request actually landing.
 */
async function authenticatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  await ensureFreshSession(API_URL);

  const send = (): Promise<Response> => {
    const token = getAccessToken();
    return fetch(input, {
      ...init,
      credentials: "include",
      headers: {
        ...init?.headers,
        // Cannot be set by a cross-origin form post, so it doubles as the CSRF
        // control alongside SameSite=Lax on the refresh cookie.
        "x-fc-client": "web",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
  };

  const response = await send();
  if (response.status !== 401) return response;

  const refreshed = await refreshSession(API_URL);
  if (!refreshed) {
    notifyExpired();
    return response;
  }
  return send();
}

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${API_URL}/trpc`,
      fetch: authenticatedFetch,
      // A gallery screen fires several queries at once; batching turns them
      // into one request. Kept modest so one slow procedure cannot hold up a
      // large batch.
      maxURLLength: 2048,
    }),
  ],
});

export type ApiError = TRPCClientErrorLike<AppRouter>;

/** The server's own human-readable message, with a safe fallback. */
export function errorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.length > 0) return message;
  }
  return "Something went wrong. Please try again.";
}

export function errorCode(error: unknown): string | undefined {
  const data = (error as { data?: { appCode?: string } } | undefined)?.data;
  return data?.appCode;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Photo metadata rarely changes under the user's feet, and a gallery
      // remounting on every tab focus is jarring.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const code = errorCode(error);
        // Never retry a decision the server already made deliberately.
        if (
          code &&
          [
            "UNAUTHENTICATED",
            "FORBIDDEN",
            "NOT_FOUND",
            "QUOTA_EXCEEDED",
            "VALIDATION",
          ].includes(code)
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});
